import { useQueries } from "@tanstack/react-query";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  FileResponse,
  Passage,
  PassagesResponse,
} from "@patent-studio/api-types";
import { DocumentPanel, type Turned } from "./DocumentPanel";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { Sheet } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { documentFormatLabel } from "@/lib/constants";
import { passagesQuery } from "@/hooks/use-invention";
import { fileUrlQuery } from "@/hooks/use-files";
import { TONES, type Highlight, type HighlightTone } from "./HighlightLayer";
import { OpenElsewhere } from "./OpenElsewhere";
import type { ScrollTarget } from "./PdfPages";

// pdf.js is its own chunk, fetched the first time a drawer opens and never
// before; the walk test under `PdfPages` pins that nothing imports it statically.
const PdfPages = lazy(() =>
  import("./PdfPages").then((module) => ({ default: module.PdfPages })),
);

/** A passage already placed, as a citation of the novelty run is: its page, its box, and its words for a format with no pages. */
export type PlacedPassage = Pick<Passage, "page" | "box" | "text">;

/**
 * What is marked in one document: the element whose passages the route
 * answers, or passages already in hand, each read in one tone.
 */
export type Marked =
  | { tone: HighlightTone; elementId: string }
  | { tone: HighlightTone; passages: readonly PlacedPassage[] };

/** One document the drawer can turn to, and what is marked in it. */
export interface DrawerDocument {
  file: FileResponse;
  /** What is marked; null opens the file plain, as a prior art row does. */
  marked: Marked | null;
}

/**
 * What is known of one document's passages, from the route or the caller's
 * hand: the passages themselves, and, where the route answered, the label
 * and page count the record holds. Null while nothing is marked.
 */
interface Served {
  label: string | null;
  pages: number | null;
  passages: readonly PlacedPassage[];
}

/** The route's answer, or the passages in hand, as one shape; null for a document opened plain or still being asked. */
function servedOf(
  document: DrawerDocument,
  answered: PassagesResponse | undefined,
): Served | null {
  if (answered !== undefined) {
    return {
      label: answered.document_label,
      pages: answered.page_count,
      passages: answered.passages,
    };
  }
  if (document.marked !== null && "passages" in document.marked) {
    return { label: null, pages: null, passages: document.marked.passages };
  }
  return null;
}

/**
 * One place in the walk: a document, and which of its passages, or the
 * document alone when it has none to show (nothing marked, no passage
 * answered, or the passages could not be read) so it can still be turned to.
 */
interface Stop {
  document: number;
  passage: number | null;
}

const PDF = "application/pdf";

/** A format the browser reads as it is: plain text, framed whole, whatever is marked in it. */
function framed(contentType: string): boolean {
  return contentType.startsWith("text/");
}

/**
 * The stored document in a drawer: a PDF page by page with the cited
 * passages marked, or a format with no geometry as the cited blocks' text.
 *
 * The drawer walks every passage the element cites, across pages and across
 * the documents it was given, with Previous and Next; the header names the
 * document, the passage's page, the format and the page count. Opened, it
 * asks for the file's URL and the passages; closed, it keeps neither.
 */
export function DocumentDrawer({
  taskId,
  documents,
  openAt = 0,
  onClose,
}: {
  taskId: string;
  /** What is open, in the order Next walks it, or null while the drawer is closed. */
  documents: DrawerDocument[] | null;
  /** The document to open on, by its place in the list; its first passage where it has one. */
  openAt?: number;
  onClose: () => void;
}) {
  const open = documents !== null && documents.length > 0;
  // Where the list stood when the drawer opened: closing puts the attorney
  // back there. Only closing; a page left for another route keeps its own.
  const scrollYRef = useRef(0);
  useEffect(() => {
    if (open) scrollYRef.current = window.scrollY;
  }, [open]);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        onClose();
        window.scrollTo(0, scrollYRef.current);
      }}
    >
      {open && <Opened taskId={taskId} documents={documents} openAt={openAt} />}
    </Sheet>
  );
}

/** Where the renderer is sent for a stop: the passage's page, a little above its box. */
function targetOf(passage: PlacedPassage): ScrollTarget {
  return { page: passage.page, top: passage.box?.top ?? 0 };
}

/**
 * The walk's places, from what the passages queries have answered so far.
 *
 * Every document in the order given, each as its marked passages in the
 * order the element cites them, or as itself alone once its passages have
 * answered with nothing (or refused), so no document the walk was given is
 * out of its reach. A document still being asked contributes nothing yet.
 * Memoised on the answers, so the same stop is the same object across renders.
 */
function useStops(
  served: readonly (Served | null)[],
  pending: readonly boolean[],
): Stop[] {
  return useMemo(
    () =>
      served.flatMap((known, i): Stop[] =>
        known !== null && known.passages.length > 0
          ? known.passages.map((_, j) => ({ document: i, passage: j }))
          : pending[i]
            ? []
            : [{ document: i, passage: null }],
      ),
    [served, pending],
  );
}

/**
 * What the renderer says back, kept with the document it said it of, so what
 * was true of the last document is never read as true of this one.
 */
function useRendererReports(index: number) {
  const [said, setSaid] = useState<{
    document: number;
    pageUnder: number | null;
    pages: number | null;
  } | null>(null);
  return {
    heard: said?.document === index ? said : null,
    onCurrentPage: useCallback(
      (pageUnder: number) => {
        setSaid((s) => ({
          document: index,
          pages: s?.document === index ? s.pages : null,
          pageUnder,
        }));
      },
      [index],
    ),
    onOpened: useCallback(
      (pages: number) => {
        setSaid((s) => ({
          document: index,
          pageUnder: s?.document === index ? s.pageUnder : null,
          pages,
        }));
      },
      [index],
    ),
  };
}

/**
 * Everything the drawer asks the server for: each document's URL, held for as
 * long as the drawer is open so turning back finds it as it was, and each
 * document's passages.
 *
 * Passages come from the route for an element, and from the caller's hand for a
 * citation already placed; a document opened plain asks for none. Structurally
 * shared, so the arrays keep their identity until an answer changes.
 */
function useDrawerDocuments(taskId: string, documents: DrawerDocument[]) {
  const urls = useQueries({
    queries: documents.map((d) => fileUrlQuery(taskId, d.file.file_id)),
  });
  const { served, pending, failed } = useQueries({
    queries: documents.map((d) =>
      passagesQuery(
        taskId,
        d.file.file_id,
        d.marked !== null && "elementId" in d.marked
          ? d.marked.elementId
          : null,
      ),
    ),
    combine: (results) => ({
      served: results.map((r, i) => servedOf(documents[i], r.data)),
      pending: results.map((r) => r.isPending && r.fetchStatus !== "idle"),
      failed: results.some((r) => r.isError),
    }),
  });
  return { urls, served, pending, failed, stops: useStops(served, pending) };
}

/** Mounted only while open, so its state starts afresh with every opening. */
function Opened({
  taskId,
  documents,
  openAt,
}: {
  taskId: string;
  documents: DrawerDocument[];
  openAt: number;
}) {
  const { urls, served, pending, failed, stops } = useDrawerDocuments(
    taskId,
    documents,
  );

  // Where the walk stands: the stop the attorney chose, or, until they move,
  // the opening document's first. Held as the stop itself rather than an
  // index, since an earlier document's passages answering later would move
  // every index after them.
  const [moved, setMoved] = useState<Stop | null>(null);
  const [target, setTarget] = useState<ScrollTarget | null>(null);
  const at = Math.max(
    0,
    moved === null
      ? stops.findIndex((s) => s.document >= openAt)
      : stops.findIndex(
          (s) => s.document === moved.document && s.passage === moved.passage,
        ),
  );
  const stop: Stop | null = at < stops.length ? stops[at] : null;
  const index = stop?.document ?? 0;
  const known = served[index];
  const passage =
    stop?.passage == null ? null : (known?.passages[stop.passage] ?? null);
  const highlighted = stops.filter((s) => s.passage !== null);
  const { heard, onCurrentPage, onOpened } = useRendererReports(index);
  const counter =
    stop?.passage == null
      ? null
      : { index: highlighted.indexOf(stop) + 1, of: highlighted.length };

  // The opening stop's target is set as its passage arrives; every later
  // target is set as the attorney moves, so a return to a stop scrolls again.
  if (target === null && passage !== null) setTarget(targetOf(passage));

  const go = (next: number) => {
    const to = stops[next];
    setMoved(to);
    const there =
      to.passage === null
        ? null
        : (served[to.document]?.passages[to.passage] ?? null);
    setTarget(there === null ? null : targetOf(there));
  };

  const url = urls[index];
  const turned: Turned = {
    file: documents[index].file,
    tone: documents[index].marked?.tone ?? null,
    label: known?.label ?? null,
    passages: known?.passages ?? [],
    // The first count anyone holds: the record's, the renderer's, then the file's.
    pages: known?.pages ?? heard?.pages ?? documents[index].file.page_count,
  };

  return (
    <DocumentPanel turned={turned} page={passage?.page ?? null}>
      <Controls at={at} stops={stops.length} counter={counter} onGo={go} />
      {failed && (
        <p role="alert" className="text-xs text-destructive">
          The cited passages could not be read. The document opens without them.
        </p>
      )}
      <Body
        file={turned.file}
        tone={turned.tone}
        passages={turned.passages}
        url={url.data ?? null}
        refused={url.isError}
        ready={!pending[index]}
        scrollTo={target}
        onOpened={onOpened}
        onCurrentPage={onCurrentPage}
      />
      <PageLine
        page={heard?.pageUnder ?? passage?.page ?? null}
        pages={turned.pages}
      />
    </DocumentPanel>
  );
}

/** "Page X of Y" under the document, centred, apart from the walk so the walk's buttons read as its own. */
function PageLine({
  page,
  pages,
}: {
  page: number | null;
  pages: number | null;
}) {
  if (page === null || pages === null) return null;
  return (
    <p className="text-center text-[12px] text-muted-foreground">
      Page {String(page)} of {String(pages)}
    </p>
  );
}

/** The walk between the highlighted passages: which this is, and Previous and Next. */
function Controls({
  at,
  stops,
  counter,
  onGo,
}: {
  at: number;
  /** How many places the walk has, documents alone included. */
  stops: number;
  /** Which highlighted passage this is of how many, or null on a document alone. */
  counter: { index: number; of: number } | null;
  onGo: (next: number) => void;
}) {
  if (stops < 2 && counter === null) return null;
  return (
    <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
      {counter !== null && (
        <span>
          Highlighted passages {String(counter.index)} of {String(counter.of)}
        </span>
      )}
      <Button
        variant="secondary"
        size="dense"
        disabled={at === 0}
        onClick={() => {
          onGo(at - 1);
        }}
      >
        Previous
      </Button>
      <Button
        variant="secondary"
        size="dense"
        disabled={at >= stops - 1}
        onClick={() => {
          onGo(at + 1);
        }}
      >
        Next
      </Button>
    </div>
  );
}

/** The document itself: opening, refused, drawn page by page, or its cited text. */
function Body({
  file,
  tone,
  passages,
  url,
  refused,
  ready,
  scrollTo,
  onOpened,
  onCurrentPage,
}: {
  file: FileResponse;
  tone: HighlightTone | null;
  passages: readonly PlacedPassage[];
  /** The signed URL, or null while it is on its way. */
  url: string | null;
  /** The URL will not come. */
  refused: boolean;
  /** Whether the passages have answered, so the marks land with the first paint. */
  ready: boolean;
  scrollTo: ScrollTarget | null;
  onOpened: (pages: number) => void;
  onCurrentPage: (page: number) => void;
}) {
  const highlights = useMemo<Highlight[]>(() => {
    if (tone === null) return [];
    return passages.flatMap((p, j) =>
      p.box === null
        ? []
        : [
            {
              id: `${file.file_id}:${String(j)}`,
              page: p.page,
              box: p.box,
              tone,
              label: TONES[tone].label,
            },
          ],
    );
  }, [file.file_id, tone, passages]);

  const opening = (
    <p role="status" className="p-6 text-sm text-muted-foreground">
      Opening {file.original_filename}…
    </p>
  );

  return (
    <Panel
      shape="row"
      pad="none"
      className="min-h-0 flex-1 overflow-y-auto bg-muted/40"
    >
      {refused ? (
        <p role="alert" className="p-6 text-sm text-destructive">
          {file.original_filename} could not be opened. Close the drawer and
          open it again.
        </p>
      ) : url === null || !ready ? (
        opening
      ) : file.content_type === PDF ? (
        <Suspense fallback={opening}>
          <PdfPages
            url={url}
            highlights={highlights}
            scrollTo={scrollTo}
            onOpened={onOpened}
            onCurrentPage={onCurrentPage}
          />
        </Suspense>
      ) : framed(file.content_type) ? (
        <iframe
          title={file.original_filename}
          src={url}
          className="h-full min-h-[60vh] w-full bg-white"
        />
      ) : (
        <TextPassages file={file} url={url} tone={tone} passages={passages} />
      )}
    </Panel>
  );
}

/**
 * A format with no geometry: the cited blocks as text, page by page, each
 * marked as the passage would be on a page, and the file itself for the rest.
 */
function TextPassages({
  file,
  url,
  tone,
  passages,
}: {
  file: FileResponse;
  url: string;
  tone: HighlightTone | null;
  passages: readonly PlacedPassage[];
}) {
  const cited = passages.filter((p) => p.text !== null);
  return (
    <div className="flex flex-col gap-4 p-4 text-sm">
      {tone !== null &&
        cited.map((p, j) => (
          <section key={`${String(p.page)}:${String(j)}`}>
            <MicroLabel as="h3" className="mb-1">
              Page {String(p.page)}
            </MicroLabel>
            <mark
              data-tone={tone}
              className={cn(
                "block whitespace-pre-wrap rounded-md border-l-4 px-3 py-2 text-foreground",
                TONES[tone].markClass,
              )}
            >
              <MicroLabel size="sm" className="mb-1 block">
                {TONES[tone].label}
              </MicroLabel>
              {p.text}
            </mark>
          </section>
        ))}
      <OpenElsewhere
        url={url}
        label={`Open ${file.original_filename} in a new tab`}
      >
        <p className="text-muted-foreground">
          A {documentFormatLabel(file.content_type, file.original_filename)}{" "}
          file has no pages to draw here.
          {cited.length > 0
            ? " The cited passages are shown from its text; open the file itself for the rest."
            : " Open it in a new tab and it downloads for your usual application."}
        </p>
      </OpenElsewhere>
    </div>
  );
}
